/* eslint-disable jsx-a11y/click-events-have-key-events */

/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React from "react"

import classNames from "classnames"

import { stopPropagation } from "shared/lib/stopPropagation"
import { useEvent } from "shared/lib/useEvent"
import { useRovingTabIndex } from "shared/lib/useRovingTabIndex"
import { Action } from "shared/types/Action"
import { Direction } from "shared/types/Direction"
import { IconSize } from "shared/types/Icon"
import { WithClassName } from "shared/types/WithClassName"
import { Button, ButtonProps, ButtonVariant } from "shared/ui/buttons/Button"

import { Tooltip } from "../tooltip/Tooltip"
import "./styles/_actions-button.scss"

export enum ActionButtonsStyle {
  LIST = "action-buttons-list",
  TOOLBAR = "action-buttons-toolbar"
}

export interface ActionButtonsProps extends WithClassName {
  label?: string
  actions: Action[]
  isIconOnly?: boolean
  iconSize?: IconSize
  autoFocus?: boolean
  actionButtonsStyle?: ActionButtonsStyle
  preventFocus?: boolean
  isDisabled?: boolean
  defaultButtonVariant?: string
  showTooltips?: boolean
  "data-testid"?: string
  onAction?: () => void
}

export const ActionButtonsButton = React.memo(
  React.forwardRef(
    (
      {
        onAction,
        variant = ButtonVariant.TERTIARY,
        showTooltip = false,
        ...props
      }: React.ComponentProps<typeof Button> & {
        onAction?: () => void
        showTooltip?: boolean
      },
      ref?: React.ForwardedRef<HTMLButtonElement>
    ) => {
      const onClick: ButtonProps["onClick"] = useEvent(ev => {
        props.onClick?.(ev)
        onAction?.()
      })

      return (
        <Tooltip
          content={props.label || null}
          disabled={!showTooltip}
          placement="bottom"
        >
          <Button
            {...props}
            variant={variant || ButtonVariant.SECONDARY}
            onClick={props.onClick ? onClick : undefined}
            ref={ref}
          />
        </Tooltip>
      )
    }
  )
)

export const ActionButtons = React.memo(
  ({
    actions,
    label,
    className,
    isIconOnly,
    actionButtonsStyle = ActionButtonsStyle.LIST,
    autoFocus,
    preventFocus,
    isDisabled,
    iconSize = IconSize.SMALL,
    defaultButtonVariant,
    showTooltips = false,
    onAction,
    ...rest
  }: ActionButtonsProps) => {
    const { containerProps, getElementProps } =
      useRovingTabIndex<HTMLButtonElement>({
        elementCount: actions.length,
        direction: Direction.VERTICAL,
        preventFocus,
        disableRovingTabIndex: true
      })

    return (
      <div
        role="group"
        aria-label={label}
        className={classNames("action-buttons", actionButtonsStyle, className)}
        onClick={stopPropagation}
        {...rest}
        {...containerProps}
      >
        <Tooltip.Provider disabled={!showTooltips}>
          {actions.map((action, index) => (
            <ActionButtonsButton
              iconSize={iconSize}
              autoFocus={autoFocus && index === 0}
              data-testid={action.id}
              key={action.id}
              variant={defaultButtonVariant}
              {...action}
              onAction={onAction}
              disabled={action.disabled || isDisabled}
              isIconOnly={isIconOnly || action.isIconOnly}
              {...getElementProps(index)}
              className={classNames(
                "action-buttons__button",
                className ? `${className}__button` : undefined,
                action.className
              )}
              showTooltip={showTooltips}
            />
          ))}
        </Tooltip.Provider>
      </div>
    )
  }
)
